import React, { useState } from 'react';
import { BgColorsOutlined, LinkOutlined, CheckCircleOutlined, DesktopOutlined, EditOutlined, StarOutlined } from '@ant-design/icons';
import { Form, Input, Button, Select, Checkbox, Upload, Alert, Card, Typography, Space, Divider, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  createDesProject,
  getDesProjects,
} from '../../features/projects/desprojectSlice';
import { designToolsOptions } from '../../assets/data/stack';

const { TextArea } = Input;
const { Title, Text } = Typography;

const CreateDesForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.desprojects);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleFormSubmit = async (values) => {
    try {
      console.log('=== FORM SUBMIT DEBUG ===');
      console.log('Form values:', values);
      console.log('File from values:', values.file);
      console.log('File length:', values.file?.length);
      if (values.file && values.file[0]) {
        console.log('First file object:', values.file[0]);
        console.log('originFileObj:', values.file[0].originFileObj);
        console.log('File details:', {
          name: values.file[0].name,
          size: values.file[0].size,
          type: values.file[0].type
        });
      }
      console.log('========================');
      
      const { name, desc, link, status, type, priority, stacks, file } = values;

      // Validation checks
      if (!name || !desc || !type || !status) {
        console.error('Missing required fields');
        setFailed(true);
        return;
      }

      if (!stacks || (Array.isArray(stacks) && stacks.length === 0)) {
        console.error('No stacks selected');
        setFailed(true);
        return;
      }

      // Check if file is required - based on your route, it IS required
      if (!file || !file.length) {
        console.error('No file selected - this is required!');
        alert('Please select an image file');
        setFailed(true);
        return;
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('desc', desc);
      formData.append('link', link || '');
      formData.append('type', type);
      // Convert boolean status to string that backend expects
      formData.append('status', status.toString());
      formData.append('priority', priority || '');
      formData.append('stacks', Array.isArray(stacks) ? stacks.join(',') : stacks);
      
      // Handle file upload - this is REQUIRED
      const fileObj = file[0].originFileObj || file[0];
      if (fileObj) {
        console.log('Adding file to FormData:', fileObj);
        formData.append('file', fileObj);
      } else {
        console.error('No valid file object found');
        alert('Invalid file selected');
        setFailed(true);
        return;
      }

      console.log('=== FINAL FORMDATA ===');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      console.log('=====================');

      await dispatch(createDesProject({ formData })).unwrap();
      await dispatch(getDesProjects());
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      form.resetFields();
    } catch (err) {
      console.error('Create project error:', err);
      console.error('Error details:', err.message, err.stack);
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    console.log('Error fields:', errorInfo.errorFields);
    
    // Show specific validation errors
    errorInfo.errorFields.forEach((field, index) => {
      console.log(`Field ${index + 1}:`, field);
    });
    
    setFailed(true);
    setTimeout(() => {
      setFailed(false);
    }, 3000);
  };

 

  const groupedTools = designToolsOptions.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = [];
    }
    acc[tool.category].push(tool);
    return acc;
  }, {});

  // Custom file upload props
  const uploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Image must be smaller than 10MB!');
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    onChange(info) {
      console.log('Upload onChange:', info);
      // Update form field value when file list changes
      form.setFieldsValue({ file: info.fileList });
    },
    onRemove: (file) => {
      console.log('File removed:', file);
      // Update form when file is removed
      const newFileList = form.getFieldValue('file')?.filter(item => item.uid !== file.uid) || [];
      form.setFieldsValue({ file: newFileList });
    },
    customRequest: ({ file, onSuccess }) => {
      // Don't actually upload, just mark as success for form validation
      console.log('Custom request for file:', file);
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8 text-center">
        <Space direction="vertical" size="small">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <BgColorsOutlined className="text-2xl text-accent-blue" />
            <Title level={2} className="m-0 text-text-primary font-semibold">
              Create Design Project
            </Title>
          </div>
          <Text className="text-text-secondary text-base">
            Showcase your creative design work and visual storytelling expertise
          </Text>
        </Space>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Design project created successfully!"
          description="Your design project has been added to your portfolio."
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}
      
      {failed && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Submission Failed"
          description="Please check all required fields and try again."
          type="error"
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Main Form Card */}
      <Card 
        className="shadow-medium border border-border-light rounded-xl bg-white"
        styles={{ body: { padding: '32px' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={onFinishFailed}
          size="large"
          className="space-y-6"
        >
          {/* Basic Information Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <CheckCircleOutlined className="mr-2 text-accent-blue" />
              Basic Information
            </Title>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item 
                label={<span className="text-text-primary font-medium">Project Name</span>}
                name="name" 
                rules={[{ required: true, message: 'Please enter a project name' }]}
                className="mb-0"
              >
                <Input 
                  placeholder="Enter your design project name"
                  className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
                />
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Design Type</span>}
                name="type"
                rules={[{ required: true, message: 'Please select a design type' }]}
                className="mb-0"
              >
                <Select 
                  placeholder="Select design type"
                  className="h-12"
                  popupClassName="border border-border-light rounded-lg"
                >
                  <Select.Option value="ui">🎨 UI Design</Select.Option>
                  <Select.Option value="ux">🧠 UX Research & Design</Select.Option>
                  <Select.Option value="combined">🎯 UI/UX Combined</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Project Description</span>}
              name="desc" 
              rules={[{ required: true, message: 'Please enter a description' }]}
              className="mt-6"
            >
              <TextArea 
                rows={4} 
                placeholder="Describe your design project, design process, user problems solved, and key design decisions..."
                className="border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue resize-none"
              />
            </Form.Item>
          </div>

          <Divider className="border-border-light" />
          
          {/* Priority Section */}
          <div className="mb-8">
            <Title
              level={4}
              className="text-text-primary mb-4 flex items-center"
            >
              <StarOutlined className="mr-2 text-accent-blue" />
              Project Priority
            </Title>

            <Form.Item
              label={
                <span className="text-text-primary font-medium">
                  Priority Level
                </span>
              }
              name="priority"
              className="mb-0"
            >
              <Select
                placeholder="Select priority (optional)"
                className="h-12 max-w-xs"
                allowClear
                popupClassName="border border-border-light rounded-lg"
              >
                <Select.Option value="featured">⭐ Featured Project</Select.Option>
                <Select.Option value="other">📂 Other Project</Select.Option>
              </Select>
            </Form.Item>
            
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg mt-4">
              <Text className="text-purple-700 text-sm">
                <strong>💡 Priority Info:</strong> Only one design project can be marked as "Featured" at a time. 
                Featured projects get special highlighting in your portfolio.
              </Text>
            </div>
          </div>
          <Divider className="border-border-light" />

          {/* Project Details Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <DesktopOutlined className="mr-2 text-accent-blue" />
              Project Details
            </Title>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item 
                label={<span className="text-text-primary font-medium">Live Preview / Prototype</span>}
                name="link"
                className="mb-0"
              >
                <Input 
                  placeholder="https://figma.com/proto/... or live site URL"
                  prefix={<LinkOutlined className="text-neutral-400" />}
                  className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
                />
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Project Status</span>}
                name="status"
                rules={[{ required: true, message: 'Please select project status' }]}
                className="mb-0"
              >
                <Select 
                  placeholder="Select project status"
                  className="h-12"
                >
                  <Select.Option value="false">🚧 In Progress</Select.Option>
                  <Select.Option value="true">✅ Completed</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </div>

          <Divider className="border-border-light" />

          {/* Design Tools Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <BgColorsOutlined className="mr-2 text-accent-blue" />
              Design Tools & Software
            </Title>
            
            <Form.Item 
              name="stacks"
              rules={[{ required: true, message: 'Please select at least one design tool' }]}
              className="mb-0"
            >
              <Checkbox.Group className="w-full">
                <div className="space-y-6">
                  {Object.entries(groupedTools).map(([category, tools]) => (
                    <div key={category} className="bg-bg-secondary p-4 rounded-lg border border-border-light">
                      <Text className="text-text-primary font-semibold text-sm uppercase tracking-wide mb-3 block">
                        {category}
                      </Text>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {tools.map((tool) => (
                          <Checkbox 
                            key={tool.value} 
                            value={tool.value}
                            className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                          >
                            <span className="text-sm">{tool.label}</span>
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {/* Additional Tools Section */}
                  <div className="bg-neutral-50 p-4 rounded-lg border border-border-light">
                    <Text className="text-text-secondary text-xs mb-2 block">
                      💡 <strong>Pro Tip:</strong> Select the primary tools you used for this project. This helps potential clients understand your design capabilities and workflow.
                    </Text>
                  </div>
                </div>
              </Checkbox.Group>
            </Form.Item>
          </div>

          <Divider className="border-border-light" />

          {/* Design Preview Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <BgColorsOutlined className="mr-2 text-accent-blue" />
              Design Preview
            </Title>
            
            <Form.Item
              name="file"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
              rules={[
                { 
                  required: true,
                  message: 'Please upload a design preview image' 
                },
                {
                  validator: (_, fileList) => {
                    if (!fileList || fileList.length === 0) {
                      return Promise.reject(new Error('Please upload a design preview image'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              className="mb-4"
            >
              <div className="border-2 border-dashed border-border-light rounded-xl bg-bg-secondary hover:border-accent-blue hover:bg-accent-blue/5 transition-all duration-300 overflow-hidden">
                <Upload.Dragger 
                  {...uploadProps}
                  maxCount={1}
                  accept="image/*"
                  className="!border-0 !bg-transparent"
                  style={{
                    padding: '32px 24px',
                    background: 'transparent'
                  }}
                >
                  <div className="text-center">
                    <BgColorsOutlined className="text-4xl text-accent-blue mb-4" />
                    <div className="space-y-2 mb-4">
                      <Text className="text-text-primary text-base font-medium block">
                        Click to upload design preview
                      </Text>
                      <Text className="text-text-secondary text-sm block">
                        or drag and drop your image here
                      </Text>
                    </div>
                    <div className="text-xs text-neutral-400 space-y-1">
                      <div>PNG, JPG, or GIF up to 10MB</div>
                      <div>Recommended: 1200x800px for best quality</div>
                    </div>
                  </div>
                </Upload.Dragger>
              </div>
            </Form.Item>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Text className="text-text-secondary text-sm">
                <strong>💡 Pro Tip:</strong> Upload your best design showcase image. Consider using mockups, final designs, or before/after comparisons for maximum impact.
              </Text>
            </div>
          </div>

          {/* Submit Section */}
          <div className="pt-6 border-t border-border-light">
            <div className="flex justify-center">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200"
              >
                {loading ? 'Creating Design Project...' : 'Create Design Project'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateDesForm;